import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import './index.css';
import { store } from './redux/store.js';

import Login from './layout/pages/SignIn/Login.jsx';
import SignUp from './layout/pages/SignUp/SignUp.jsx';
import RootLayout from './layout/RootLayout.jsx';
import AdminRoute from './routes/AdminRoute.jsx';
import UserRoute from './routes/UserRoute.jsx';

import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';

import PortfolioAnalysisPageWrapper from '@/layout/pages/PortfolioAnalysis/PortfolioAnalysisPageWrapper';
import { CircularProgress } from '@mui/material';
import MuiXLicense from './components/MuiXLicense/MuiXLicense.jsx';
import AlarmHubPage from './layout/pages/Alarms/AlarmHubPage.jsx';
import BuildingDetailPage from './layout/pages/Buildings/BuildingDetailPage';
import BuildingPage from './layout/pages/Buildings/BuildingPage';
import BuildingPageWrapper from './layout/pages/Buildings/BuildingPageWrapper';
import BuildingRoute from './layout/pages/Buildings/BuildingRoute.jsx';
import CalendarPage from './layout/pages/Calendar/CalendarPage.jsx';
import DatahubIntegrationPage from './layout/pages/DatahubIntegration/DatahubIntegrationPage.jsx';
import EnergyXplorerPage from './layout/pages/EnergyXplorer/EnergyXplorerPage.jsx';
import GatewaysPage from './layout/pages/Gateways/GatewaysPage.jsx';
import HomePage from './layout/pages/Home/HomePage.jsx';
import PredictiveHeatControlPage from './layout/pages/JobSubscription/PredictiveHeatControlPage';
import OperationsDetailPage from './layout/pages/Operations/OperationsDetailPage.jsx';
import OperationsLayout from './layout/pages/Operations/OperationsLayout.jsx';
import OperationsPage from './layout/pages/Operations/OperationsPage.jsx';
import PortalPage from './layout/pages/Portal/PortalPage.jsx';
import PortfolioAnalysisDashboardPage from './layout/pages/PortfolioAnalysis/PortfolioAnalysisDashboardPage.jsx';
import PortfolioAnalysisPage from './layout/pages/PortfolioAnalysis/PortfolioAnalysisPage.jsx';
import PortfolioAnalysisPageOld from './layout/pages/PortfolioAnalysisOld/PortfolioAnalysisPage.jsx';
import PowerBiPage from './layout/pages/PowerBI/PowerBiPage.jsx';
import SensorsPage from './layout/pages/Sensors/SensorsPage.jsx';
import EnergyXplorerManagementPage from './layout/pages/ServiceManagement/energyxplorer/EnergyXplorerManagementPage.jsx';
import PortfolioAnalysisManagementPage from './layout/pages/ServiceManagement/portfolio-analysis/PortfolioAnalysisManagementPage.jsx';
import ServiceManagementPage from './layout/pages/ServiceManagement/ServiceManagementPage.jsx';
import SubscriptionPage from './layout/pages/Subscriptions/SubscriptionPage';
import SupportPage from './layout/pages/Support/SupportPage.jsx';
import ThemeDemoPage from './layout/pages/ThemeDemo/ThemeDemoPage.jsx';
import UnitsPage from './layout/pages/Units/UnitsPage.jsx';
import DatahubIntegrationRoute from './routes/DatahubIntegrationRoute.jsx';
import EnergyXplorerRoute from './routes/EnergyXplorerRoute.jsx';
import ErrorBoundary from './routes/ErrorBoundary.jsx';
import PortfolioAnalysisRoute from './routes/PortfolioAnalysisRoute.jsx';
import PvoRoute from './routes/PvoRoute.jsx';
import OperationsPageWrapper from './layout/pages/Operations/OperationsPageWrapper';

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        path: 'sign-in',
        element: <Login />,
      },
      {
        path: 'sign-up',
        element: <SignUp />,
      },
      {
        path: 'support',
        element: (
          <Suspense fallback={<CircularProgress />}>
            <SupportPage />
          </Suspense>
        ),
      },
      {
        index: true,
        element: <HomePage />,
        errorElement: <ErrorBoundary />,
      },
      {
        element: <PvoRoute />,
        children: [
          {
            path: 'savings-overview',
            element: <PortalPage />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'calendar',
            element: <CalendarPage />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      {
        element: <EnergyXplorerRoute />,
        children: [
          {
            path: 'energyxplorer',
            element: <EnergyXplorerPage />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      {
        element: <DatahubIntegrationRoute />,
        children: [
          {
            path: 'datahub-integration',
            element: <DatahubIntegrationPage />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      {
        path: 'portfolio-analysis-hub',
        element: <PortfolioAnalysisRoute />,
        children: [
          {
            index: true,
            element: (
              <PortfolioAnalysisPageWrapper>
                <PortfolioAnalysisDashboardPage />
              </PortfolioAnalysisPageWrapper>
            ),
          },
          {
            path: 'building',
            element: (
              <PortfolioAnalysisPageWrapper>
                <PortfolioAnalysisDashboardPage />
              </PortfolioAnalysisPageWrapper>
            ),
          },
          {
            path: '*',
            element: (
              <PortfolioAnalysisPageWrapper>
                <PortfolioAnalysisPage />
              </PortfolioAnalysisPageWrapper>
            ),
          },
        ],
        errorElement: <ErrorBoundary />,
      },
      {
        element: <UserRoute />,
        children: [
          {
            path: 'powerbi',
            element: <PowerBiPage />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      {
        element: <AdminRoute />,
        children: [
          {
            path: 'operations',
            element: <OperationsLayout />,
            children: [
              {
                index: true,
                element: (
                  <OperationsPageWrapper>
                    <OperationsPage />
                  </OperationsPageWrapper>
                ),
                errorElement: <ErrorBoundary />,
              },
              {
                path: ':groupKey',
                element: <OperationsDetailPage />,
                errorElement: <ErrorBoundary />,
              },
            ],
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'service-management',
            errorElement: <ErrorBoundary />,
            children: [
              { index: true, element: <ServiceManagementPage /> },
              {
                path: 'energyxplorer',
                element: <EnergyXplorerManagementPage />,
              },
              {
                path: 'portfolio-analysis',
                element: <PortfolioAnalysisManagementPage />,
              },
            ],
          },
          {
            path: 'alarms',
            element: <AlarmHubPage />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'units',
            element: <UnitsPage />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'gateways',
            element: <GatewaysPage />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sensors',
            element: <SensorsPage />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'demo',
            element: <ThemeDemoPage />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'PVO',
            element: <BuildingRoute />, // Manages the context for building
            children: [
              {
                index: true,
                element: <Navigate to="buildings" replace />,
              },
              {
                path: 'buildings',
                element: (
                  <BuildingPageWrapper>
                    <BuildingPage />
                  </BuildingPageWrapper>
                ),
                errorElement: <ErrorBoundary />,
              },
              {
                path: 'subscriptions',
                element: (
                  <BuildingPageWrapper>
                    <SubscriptionPage />
                  </BuildingPageWrapper>
                ),
                errorElement: <ErrorBoundary />,
              },
              {
                path: 'buildings/:buildingName',
                element: (
                  <BuildingPageWrapper>
                    <BuildingDetailPage />
                  </BuildingPageWrapper>
                ),
                errorElement: <ErrorBoundary />,
              },
              {
                path: 'buildings/:buildingName/:subscriptionName',
                element: (
                  <BuildingPageWrapper>
                    <PredictiveHeatControlPage />
                  </BuildingPageWrapper>
                ),
                errorElement: <ErrorBoundary />,
              },
              {
                path: 'subscriptions/:subscriptionName',
                element: (
                  <BuildingPageWrapper>
                    <PredictiveHeatControlPage />
                  </BuildingPageWrapper>
                ),
                errorElement: <ErrorBoundary />,
              },
            ],
            errorElement: <ErrorBoundary />,
          },
        ],
        errorElement: <ErrorBoundary />,
      },
      {
        path: '*',
        element: <ErrorBoundary />,
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Provider store={store}>
      <RouterProvider router={router} />
      <MuiXLicense />
    </Provider>
  </React.StrictMode>,
);
